import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Step definitions matching backend
export const ONBOARDING_STEPS = {
  WELCOME_BASIC_INFO: 'welcome_basic_info', // Merged step
  WELCOME_TERMS: 'welcome_terms', // Backend legacy name
  BASIC_INFO: 'basic_info', // Backend legacy name
  GOALS_SELECTION: 'goals_selection',
  SOCIAL_PROOF: 'social_proof',
  PACKAGE_OFFER: 'package_offer',
  DISPUTE_WORKFLOW_EDUCATION: 'dispute_workflow_education', // NEW
  DISPUTE_TYPE_SELECTION: 'dispute_type_selection', // NEW: Factual vs Metro 2
  PRE_CREDENTIALS_VIDEO: 'pre_credentials_video', // Legacy name
  CREDIT_MONITORING_CREDENTIALS: 'credit_monitoring_credentials',
  REPORT_IMPORT_LOADING: 'report_import_loading',
  DASHBOARD_READY: 'dashboard_ready',
  DASHBOARD_FIRST_USE_EDUCATION: 'dashboard_first_use_education',
};

// New 10-step flow (with dispute type selection)
export const STEP_ORDER = [
  'welcome_basic_info',      // Step 1 - Merged
  'goals_selection',         // Step 2
  'social_proof',            // Step 3
  'package_offer',           // Step 4
  'dispute_workflow_education', // Step 5 - NEW
  'dispute_type_selection',  // Step 6 - NEW
  'credit_monitoring_credentials', // Step 7
  'report_import_loading',   // Step 8
  'dashboard_ready',         // Step 9
  'dashboard_first_use_education', // Step 10 (in-dashboard)
];

// Backend step names for API communication
const BACKEND_STEP_ORDER = [
  ONBOARDING_STEPS.WELCOME_TERMS,
  ONBOARDING_STEPS.BASIC_INFO,
  ONBOARDING_STEPS.GOALS_SELECTION,
  ONBOARDING_STEPS.SOCIAL_PROOF,
  ONBOARDING_STEPS.PACKAGE_OFFER,
  ONBOARDING_STEPS.PRE_CREDENTIALS_VIDEO,
  ONBOARDING_STEPS.CREDIT_MONITORING_CREDENTIALS,
  ONBOARDING_STEPS.REPORT_IMPORT_LOADING,
  ONBOARDING_STEPS.DASHBOARD_READY,
  ONBOARDING_STEPS.DASHBOARD_FIRST_USE_EDUCATION,
];

const defaultState = {
  current_step: 'welcome_terms',
  current_step_index: 1,
  completed_steps: [],
  skipped_steps: [],
  terms_accepted: false,
  first_name: null,
  last_name: null,
  email: null,
  phone: null,
  basic_info_completed: false,
  selected_goals: [],
  goals_completed: false,
  social_proof_viewed: false,
  selected_package: null,
  dispute_type: null, // NEW: 'factual' or 'metro2'
  dispute_education_completed: false, // NEW
  pre_credentials_video_seen: false,
  pre_credentials_video_completed: false,
  credentials_submitted: false,
  selected_provider: null,
  import_started: false,
  import_id: null,
  import_status: null,
  import_completed: false,
  dashboard_ready_shown: false,
  dashboard_intro_video_seen: false,
  dashboard_education_completed: false,
  onboarding_completed: false,
  has_imported_report: false,
  report_id: null,
};

const OnboardingContext = createContext(null);

export const OnboardingProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [state, setState] = useState(defaultState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);

  // Check if user is Auto Starter type
  const isAutoStarter = user?.role === 'self_repair_customer' || 
    (user?.role === 'client' && !user?.agency_id && !user?.workspace_id);

  // Fetch onboarding state from backend
  const fetchOnboardingState = useCallback(async () => {
    if (!token || !isAutoStarter) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/auto-starter/onboarding/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.applicable) {
        setState(response.data.state);
      }
    } catch (err) {
      console.error('Failed to fetch onboarding state:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, isAutoStarter]);

  // Fetch routing info
  const fetchRouteInfo = useCallback(async () => {
    if (!token || !isAutoStarter) return null;

    try {
      const response = await axios.get(`${API}/auto-starter/onboarding/route`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRouteInfo(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch route info:', err);
      return null;
    }
  }, [token, isAutoStarter]);

  // Update a specific step
  const updateStep = useCallback(async (step, data = {}, markCompleted = false, markSkipped = false) => {
    if (!token) return null;

    try {
      const response = await axios.post(`${API}/auto-starter/onboarding/step`, {
        step,
        data,
        mark_completed: markCompleted,
        mark_skipped: markSkipped,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setState(response.data.state);
      return response.data;
    } catch (err) {
      console.error('Failed to update step:', err);
      throw err;
    }
  }, [token]);

  // Track video completion
  const trackVideoCompletion = useCallback(async (videoType, watchedSeconds = 0, totalSeconds = 0, completed = false) => {
    if (!token) return;

    try {
      await axios.post(`${API}/auto-starter/onboarding/video-completion`, {
        video_type: videoType,
        watched_seconds: watchedSeconds,
        total_seconds: totalSeconds,
        completed,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update local state
      if (videoType === 'pre_credentials') {
        setState(prev => ({
          ...prev,
          pre_credentials_video_seen: true,
          pre_credentials_video_completed: completed,
        }));
      } else if (videoType === 'dashboard_intro') {
        setState(prev => ({
          ...prev,
          dashboard_intro_video_seen: true,
          dashboard_education_completed: completed,
        }));
      }
    } catch (err) {
      console.error('Failed to track video:', err);
    }
  }, [token]);

  // Save package selection
  const selectPackage = useCallback(async (packageId) => {
    if (!token) return;

    try {
      await axios.post(`${API}/auto-starter/onboarding/package-selection`, {
        package_id: packageId,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setState(prev => ({
        ...prev,
        selected_package: packageId,
      }));
    } catch (err) {
      console.error('Failed to select package:', err);
    }
  }, [token]);

  // Mark dashboard ready as shown
  const markDashboardReadyShown = useCallback(async () => {
    if (!token) return;

    try {
      await axios.post(`${API}/auto-starter/onboarding/mark-dashboard-ready-shown`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setState(prev => ({
        ...prev,
        dashboard_ready_shown: true,
        current_step: 'dashboard_first_use_education',
        current_step_index: 10,
      }));
    } catch (err) {
      console.error('Failed to mark dashboard ready:', err);
    }
  }, [token]);

  // Complete dashboard education
  const completeDashboardEducation = useCallback(async () => {
    if (!token) return;

    try {
      await axios.post(`${API}/auto-starter/onboarding/complete-education`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setState(prev => ({
        ...prev,
        dashboard_education_completed: true,
        onboarding_completed: true,
        current_step: 'completed',
        current_step_index: 11,
      }));
    } catch (err) {
      console.error('Failed to complete education:', err);
    }
  }, [token]);

  // Check if can access dashboard
  const checkDashboardAccess = useCallback(async () => {
    if (!token || !isAutoStarter) return { can_access: true };

    try {
      const response = await axios.get(`${API}/auto-starter/onboarding/can-access-dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (err) {
      console.error('Failed to check dashboard access:', err);
      return { can_access: true }; // Allow access on error
    }
  }, [token, isAutoStarter]);

  // Get step index - maps backend step names to UI step numbers
  const getStepIndex = (step) => {
    // Map old backend step names to new merged UI steps
    const stepMapping = {
      'welcome_terms': 1,
      'basic_info': 1, // Merged into step 1
      'welcome_basic_info': 1,
      'goals_selection': 2,
      'social_proof': 3,
      'package_offer': 4,
      'pre_credentials_video': 5, // Legacy maps to dispute education
      'dispute_workflow_education': 5,
      'dispute_type_selection': 6,
      'credit_monitoring_credentials': 7,
      'report_import_loading': 8,
      'dashboard_ready': 9,
      'dashboard_first_use_education': 10,
      'completed': 10,
    };
    return stepMapping[step] || 1;
  };

  // Get next step
  const getNextStep = (currentStep) => {
    const index = STEP_ORDER.indexOf(currentStep);
    if (index >= 0 && index < STEP_ORDER.length - 1) {
      return STEP_ORDER[index + 1];
    }
    return currentStep;
  };

  // Get previous step
  const getPreviousStep = (currentStep) => {
    const index = STEP_ORDER.indexOf(currentStep);
    if (index > 0) {
      return STEP_ORDER[index - 1];
    }
    return currentStep;
  };

  // Navigate to next step
  const goToNextStep = async () => {
    const nextStep = getNextStep(state.current_step);
    await updateStep(state.current_step, {}, true);
    return nextStep;
  };

  // Navigate to previous step
  const goToPreviousStep = () => {
    const prevStep = getPreviousStep(state.current_step);
    setState(prev => ({
      ...prev,
      current_step: prevStep,
      current_step_index: getStepIndex(prevStep),
    }));
    return prevStep;
  };

  // Initialize on mount
  useEffect(() => {
    if (isAutoStarter) {
      fetchOnboardingState();
    } else {
      setLoading(false);
    }
  }, [fetchOnboardingState, isAutoStarter]);

  const value = {
    // State
    state,
    loading,
    error,
    routeInfo,
    isAutoStarter,
    
    // Actions
    fetchOnboardingState,
    fetchRouteInfo,
    updateStep,
    trackVideoCompletion,
    selectPackage,
    markDashboardReadyShown,
    completeDashboardEducation,
    checkDashboardAccess,
    
    // Navigation helpers
    getStepIndex,
    getNextStep,
    getPreviousStep,
    goToNextStep,
    goToPreviousStep,
    
    // Constants
    STEPS: ONBOARDING_STEPS,
    STEP_ORDER,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export default OnboardingContext;
